import * as React from "react"
import { useController, useFormContext } from "react-hook-form"
import { FormField } from "./types"
import { useCallback } from "react"
import { WithParticleOverlay } from "./WithParticleOverlay"
import { TextEditor, TextEditorProps } from "../TextEditor/TextEditor"

import styles from "./Forms.module.scss"
import { stripHtml } from "string-strip-html"
import { trackFormEvent } from "./utils"
import { TextEditorControllerContextProvider, TextEditorControlsSetter } from "../TextEditor/TextEditor.context"

export interface Props extends FormField {
  defaultValue?: string
  hideToolbar?: boolean
  allowCreateVariables?: boolean
  allowSetVariableValues?: boolean
  allowCustomVariables?: boolean
  singleLine?: boolean
  placeholder?: string
  autoFocus?: boolean
  withImages?: boolean
  disabledAssetTypes?: string[]
  onFocus?: () => void
  onBlur?: () => void
  onVariableSelected?: (value: string) => void
  onVariableMenuOpen?: () => void
  onVariableMenuClose?: () => void
  variableOptionFilter?(variableOption: string): boolean
  onContentChange?(content: string): void
  setControls?: TextEditorControlsSetter
  classes?: Pick<TextEditorProps, "classes">["classes"]
}

const RichTextField = ({
  defaultValue,
  hideToolbar,
  className,
  name,
  label,
  rules,
  disabled,
  loading,
  allowCreateVariables,
  allowSetVariableValues,
  allowCustomVariables,
  singleLine,
  placeholder,
  autoFocus,
  tracking,
  withImages,
  disabledAssetTypes,
  classes,
  onFocus,
  onBlur,
  onContentChange,
  onVariableSelected,
  onVariableMenuOpen,
  onVariableMenuClose,
  setControls,
  variableOptionFilter,
}: Props) => {
  const { control, formState } = useFormContext()
  const controller = useController({ name, control, rules })
  const initialValueInForm = React.useRef(controller.field.value)
  const fieldOnChange = controller.field.onChange

  const error = formState.errors[name]
  const errorProps = error ? { error: error.message?.toString() } : {}

  const handleEditorHtmlChange = useCallback(
    (html: string) => {
      fieldOnChange(html)
      onContentChange?.(html)
    },
    [fieldOnChange, onContentChange]
  )

  const handleBlur = useCallback(async () => {
    onBlur?.()

    if (tracking?.shouldTrackValueChange && stripHtml(controller.field.value || "").result.trim()) {
      await trackFormEvent("Form Value Provided", tracking, name, label)
    }
  }, [controller.field.value, label, name, onBlur, tracking])

  return (
    <WithParticleOverlay
      id={name}
      classes={{ root: className, particlesClassName: styles.richTextFieldParticles }}
      loading={loading}
    >
      <TextEditorControllerContextProvider setControls={setControls}>
        <TextEditor
          classes={classes}
          disabled={disabled}
          initialText={defaultValue || initialValueInForm.current}
          onHtmlChange={handleEditorHtmlChange}
          label={label}
          hideToolbar={hideToolbar}
          allowCreateVariables={allowCreateVariables}
          allowSetVariableValues={allowSetVariableValues}
          allowCustomVariables={allowCustomVariables}
          singleLine={singleLine}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onFocus={onFocus}
          onBlur={handleBlur}
          onVariableSelected={onVariableSelected}
          onVariableMenuOpen={onVariableMenuOpen}
          onVariableMenuClose={onVariableMenuClose}
          variableOptionFilter={variableOptionFilter}
          withImages={withImages}
          disabledAssetTypes={disabledAssetTypes}
          {...errorProps}
        />
      </TextEditorControllerContextProvider>
    </WithParticleOverlay>
  )
}

export default RichTextField
